import {IUserStoreState, userSelector} from '@stencil/user-reducer';
import styled from 'styled-components';
import {pushga} from 'ga-utils';
import {gaEcomThunk} from '@stencil/hotels-ga/src/thunks';
import {gaConfig} from '@stencil/hotels-config';
import React, {useCallback, useEffect, useRef} from 'react';
import Infinite from 'react-infinite';
import {useDispatch, useSelector} from 'react-redux';
import {ButtonBase} from '../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import HotelCard from '../../../../stories/components/hotels/SRP/HotelCard/HotelCard';
import NoResultsFound from '../../../../stories/components/hotels/SRP/NoResultsFound/NoResultsFound';
import {
  countryTextMap,
  signUpBannerSlot,
  smartEngageBannerSlot,
  srpIndexForFetchMore,
} from '../../../config/app-config';
import {SrpGAEvents} from '../../../config/ga-config';
import {useNavigateForSearchWidget} from '../../../hooks/useNavigateForSearchWidget';
import {
  countrySelectorFn,
  roomStringSelectorFn,
} from '@stencil/hotels-common-reducer/src/selectors';
import {checkIfServer} from '@stencil/hotels-utils/src/common-utils';
import {
  srpOfferDataSelectorFn,
  srpSelector,
  staticLocationsVoyagerIdsSelectorFn,
  isSrpSeoSelectorFn,
  segmentCollectionsFn,
  appliedFilterSelectorFn,
} from '../redux/selectors';
import {updateSeoFlags, resetFilterAction} from '../redux/actions';
import {fetchSrpOfferDataThunk} from '../redux/thunks';
import {IHotelData, IHotelDataType, ISrpStoreState} from '../redux/types';
import HotelListLoader from './../../../../stories/components/hotels/Loadingpage/SRPCardLoader/SRPCardLoader';
import HotelListPlaceholder from '../containers/HotelListPlaceholder';
import {getSRPAppliedFiltersText} from '../utils/filter-utils';
// move to types srp

const HotelList = ({fetchMoreFn, showViewMore, displayOverlay}) => {
  const dispatch = useDispatch();
  const srp = useSelector(srpSelector);
  const roomString = useSelector(roomStringSelectorFn);
  const country = useSelector(countrySelectorFn);
  const userDetails = useSelector(userSelector) as IUserStoreState;
  const staticLocationList = useSelector(staticLocationsVoyagerIdsSelectorFn);
  const offerData = useSelector(srpOfferDataSelectorFn);
  const isSEO = useSelector(isSrpSeoSelectorFn);
  const hasSRPFilterGAFired = useRef(false);
  const appliedFilter = useSelector(appliedFilterSelectorFn);
  const {onSearch, getSeoDetailsUrl} = useNavigateForSearchWidget();
  const segmentCollections = useSelector(segmentCollectionsFn);

  const gaHandler = (filterName, val) => {
    pushga({
      eventCategory: SrpGAEvents.srpHoverFilterClicked,
      eventAction: filterName,
      eventLabel: val,
    });
  };

  const toggleSiginPopupCaller = useCallback((label = '') => {
    if (window && document) {
      document.querySelector('#get_sign_in').click();
      gaHandler('Login_Clicked_SRP', label);
    }
    // dispatch(toggleLoginPopup(true));
  }, []);

  const fetchSrpOfferDataThunkCaller = useCallback(({hotelId, pph}) => {
    dispatch(fetchSrpOfferDataThunk({hotelId, pph}));
  }, []);

  const updateSeoViewMoreStatusCaller = useCallback(
    (status: boolean) => {
      dispatch(updateSeoFlags({isSEO, seoViewMore: status}));
    },
    [dispatch],
  );

  const gaEcomProductClickThunkCaller = useCallback(
    (productDetails: number, productPosition) => {
      dispatch(
        gaEcomThunk(gaConfig.ecommerceEvents.srpProductClick, {productDetails, productPosition}),
      );
    },
    [dispatch],
  );

  const {hasData, isLoading, srpData, srpConfig} = srp as ISrpStoreState;
  const {data: hotelListFromState, city_meta_info: cityMetaInfo, next, error} = srpData;
  const {overriderankvhids, metaPartner} = srpConfig;

  const isLoggedIn = (userDetails && userDetails.isLoggedIn) || false;

  useEffect(() => {
    if (!hasSRPFilterGAFired.current) {
      hasSRPFilterGAFired.current = true;
    } else if (hotelListFromState.length < 10) {
      var appliedFilterGaText = getSRPAppliedFiltersText(appliedFilter);
      pushga({
        event: gaConfig.HotelsDwebGenericEvent,
        eventCategory: gaConfig.SrpGAEvents.hotelSrpFilterClickHotelCountWhenLess,
        eventAction: hotelListFromState && hotelListFromState.length,
        eventLabel: appliedFilterGaText,
      });
    }
  }, [hotelListFromState]);

  if (hasData === null || (isLoading && !displayOverlay)) {
    return <HotelListLoader />;
  }

  if (
    (next && next !== 'END' && !hasData) ||
    !cityMetaInfo ||
    !hotelListFromState ||
    hotelListFromState.length === 0
  ) {
    return <NoResultsFound />;
  }

  let segmentActiveIndex = 0;

  const circulateSegment = () => {
    const value = {type: 'SegmentSlider', hctx: segmentCollections[segmentActiveIndex]};

    if (segmentActiveIndex < segmentCollections.length - 1)
      segmentActiveIndex = segmentActiveIndex + 1;
    else segmentActiveIndex = 0;
    return value;
  };

  const hotelList = hotelListFromState.reduce((acc, item, index) => {
    const isInternational = country !== countryTextMap.india.key;
    if (index !== 0) {
      if (Number.isInteger((index / 5 - 1) / 2) && segmentCollections.length) {
        const segment = circulateSegment();
        acc.push(segment);
      }

      if (!isInternational && index % smartEngageBannerSlot === 0)
        acc.push({type: 'SmartEngageBanner'});
      // IH has different slotting for corona related advisory display
      if (isInternational && Number.isInteger((index / 3 - 1) / 2))
        acc.push({type: 'SmartEngageBanner'});

      if (!isLoggedIn && index % signUpBannerSlot === 0) acc.push({type: 'SignUpBanner'});
    }
    acc.push(item);
    return acc;
  }, []);

  // ! following function can be memoized for perf optimization
  const getElementHeight = data => {
    return data.map(item => {
      if (item.type === 'SegmentSlider') return 350;
      if (item.type === 'SmartEngageBanner') return 200;
      if (item.type === 'SignUpBanner') return 100;

      return 320;
    });
  };

  let lockAnimationInitialised = false;
  const prepareHotelListHtml = () => {
    const hotelListHtml = hotelList.map((item: IHotelData & {type: IHotelDataType}, index) => {
      const isSearchedHotel = overriderankvhids && overriderankvhids === item.hc && index === 0;
      const isMetaViewedHotel = metaPartner && isSearchedHotel;
      let showLockAnimation = false;
      if (!lockAnimationInitialised && item.dspr) {
        showLockAnimation = true;
        lockAnimationInitialised = true;
      }
      return (
        <React.Fragment key={'hotelItem' + index + item.hn}>
          <HotelListPlaceholder
            fetchMoreFn={fetchMoreFn}
            showLastItemLoader={
              showViewMore ? false : hotelList.length - 1 === index && next !== 'END'
            }
            shouldFetchMore={
              showViewMore || displayOverlay
                ? false
                : hotelList.length - srpIndexForFetchMore === index && next !== 'END'
            }
            data={item}
            toggleSiginPopupCaller={toggleSiginPopupCaller}
            error={error}
            applyFilterCta={filterData => {
              // onSearch({true, item, filterData});
              onSearch({openInNewTab: true, hotelData: item, newFilterData: filterData});
            }}
          >
            {(!item.type && (
              <HotelCard
                data={item}
                cityMetaInfo={cityMetaInfo}
                srpConfig={srpConfig}
                roomString={roomString}
                country={country}
                isLoggedIn={isLoggedIn}
                staticLocationList={staticLocationList}
                isMetaViewedHotel={isMetaViewedHotel}
                isSearchedHotel={isSearchedHotel}
                navigateToDetailsPage={filterData => {
                  /*
                  if filterData is there, then onSearch redirects to SRP,
                  otherwise, it redirects to details page.
                  */
                  if (
                    !filterData ||
                    (typeof filterData === 'object' && !Object.keys(filterData).length)
                  )
                    gaEcomProductClickThunkCaller(item, index + 1);
                  onSearch({openInNewTab: true, hotelData: item, newFilterData: filterData});
                }}
                getSeoDetailsPageUrl={() => {
                  return getSeoDetailsUrl(item);
                }}
                toggleSiginPopupCaller={toggleSiginPopupCaller}
                fetchSrpOfferDataThunkCaller={fetchSrpOfferDataThunkCaller}
                offerData={offerData}
                gaHandler={gaHandler}
                showLockAnimation={showLockAnimation}
                isSEO={isSEO}
              />
            )) ||
              null}
          </HotelListPlaceholder>
        </React.Fragment>
      );
    });
    return hotelListHtml;
  };

  return (
    <>
      {(showViewMore && (
        <>
          {prepareHotelListHtml()}
          <ViewMoreHotelsWrap
            onClick={() => {
              updateSeoViewMoreStatusCaller(false);
            }}
          >
            View more hotels
          </ViewMoreHotelsWrap>
        </>
      )) || (
        <Infinite
          containerHeight={1900}
          elementHeight={getElementHeight(hotelList)}
          infiniteLoadBeginEdgeOffset={400}
          handleScroll={() => {}} // was required for displaying google ads in pre-allocated slots
          useWindowAsScrollContainer={!checkIfServer()} // should be set to false for server-side
        >
          {prepareHotelListHtml()}
        </Infinite>
      )}
    </>
  );
};

export default HotelList;

const ViewMoreHotelsWrap = styled(ButtonBase)(
  props => `
  background:${props.theme.colors.goBrandBlue.medium};
  width:100%;
  color:${props.theme.colors.goBrandBlue.base};
  font-size: ${props.theme.font.custom(18)};
  padding: ${props.theme.spacing.xxlarge};
  margin-top: ${props.theme.spacing.large};

`,
);
